import { NullableString } from '@models/app.models';
import {
  Address,
  Gender,
  Name,
  PassengerTypeCode,
  TaxTypeCode,
} from '@models/booking.models';

export interface PassengerUpdateRequest {
  apisAddress?: PassengerApisAddressRequest;
  birthCountry?: string;
  customerNumber?: string;
  dateOfBirth?: NullableString;
  discountCode?: string;
  emailAddress?: string;
  gender?: Gender;
  infant?: PassengerInfant;
  issuedByCode?: string;
  knownTravelerNumber?: string;
  name: Name;
  nationality?: string;
  passengerAddressKey?: string;
  passengerAlternateKey?: string;
  passengerKey: string;
  passengerTypeCode?: PassengerTypeCode;
  passportExpirationDate?: NullableString;
  passportIssuedDate?: NullableString;
  passportNumber?: string;
  phone?: string;
  redressNumber?: string;
  residentCountry?: string;
  residentPostalCode?: string;
  residentProvinceState?: string;
}

export interface PassengerApisAddressRequest {
  addressLine1: string | undefined;
  city: string | undefined;
  countryCode: string | undefined;
  destinationStationCode?: NullableString;
  passengerAddressKey?: NullableString;
  postalCode: string | undefined;
  provinceState: string | undefined;
}

export interface PassengerInformation {
  dateOfBirth?: string;
  familyNumber?: number;
  gender?: Gender;
  nationality?: string;
  residentCountry?: string;
}

export interface PassengerProgram {
  code: string;
  levelCode: string;
  number: string;
}

export interface PassengerFee {
  paymentNumberType: number;
  ssrCodeType: string;
  ssrNumberType: number;
  type: FeeType;
}

export enum FeeType {
  All = 0,
  Tax = 1,
  TravelFee = 2,
  ServiceFee = 3,
  PaymentFee = 4,
  PenaltyFee = 5,
  SsrFee = 6,
  NonFlightServiceFee = 7,
  UpgradeFee = 8,
  SeatFee = 9,
  BaseFare = 10,
  SpoilageFee = 11,
  NameChangeFee = 12,
  ConvenienceFee = 13,
  BaggageFee = 14,
  FareSurcharge = 15,
  PromotionDiscount = 16,
  ServiceBundle = 17,
  ExtraBagFee = 18,
  ATPCOBagFee = 19,
}

export interface PassengerBag {
  baggageKey: string;
  osTagDate?: Date;
  stationCode: string;
  taggedToCarrierCode: string;
  type: string;
}

export interface PassengerInfant {
  dateOfBirth: NullableString;
  fees?: InfantFee[];
  gender: Gender;
  name: Name;
  nationality?: string;
  residentCountry?: string;
  travelDocuments?: PassengerTravelDocument[] | null;
}

export interface InfantFee {
  code: string;
  createdDate?: Date;
  detail: string;
  flightReference: string;
  isConfirmed: boolean;
  isConfirming: boolean;
  isProtected: boolean;
  note: string;
  override: boolean;
  passengerFeeKey: string;
  serviceCharges: ServiceCharge[];
}

export interface ServiceCharge {
  amount: number;
  code: TaxTypeCode;
  collectType: CollectType;
  currencyCode: string;
  detail: string;
  foreignAmount: number;
  foreignCurrencyCode: string;
  ticketCode: string;
  type: ChargeType;
}

export enum ChargeType {
  FarePrice = 0,
  Discount = 1,
  IncludedTravelFee = 2,
  IncludedTax = 3,
  TravelFee = 4,
  Tax = 5,
  ServiceCharge = 6,
  PromotionDiscount = 7,
  ConnectionAdjustmentAmount = 8,
  AddOnsPrice = 9,
  FarePoints = 10,
  DiscountPoints = 11,
  IncludedAddOnsFee = 16,
  AddOnsFee = 17,
  AddOnsMarkup = 18,
  FareSurcharge = 19,
  AddOnsCancelFee = 21,
  Calculated = 22,
  Note = 23,
  Points = 24,
  DynamicFareAdjustment = 25,
}

export enum CollectType {
  SellerChargeable = 0,
  ExternalChargeable = 1,
  SellerNonChargeable = 2,
  ExternalNonChargeable = 3,
  ExternalChargeableImmediate = 4,
}

export enum DocumentTypeCode {
  Passport = 'P',
  TravelerNumber = 'K',
  RedressNumber = 'R',
}

export interface PassengerTravelDocument {
  birthCountry?: NullableString;
  dateOfBirth?: NullableString;
  documentTypeCode: DocumentTypeCode;
  expirationDate?: NullableString;
  gender?: Gender;
  issuedByCode?: NullableString;
  issuedDate?: NullableString;
  name?: Name;
  nationality?: NullableString;
  number: NullableString;
  personTravelDocumentKey?: string;
}

export interface PassengerAddress extends Address {
  companyName?: string;
  cultureCode?: string;
  emailAddress?: string;
  passengerAddressKey?: string;
  phone?: string;
  refusedContact?: boolean;
  sameAsPaxKey?: string;
  stationCode?: string;
  status?: ApisAddressStatus;
}

export enum ApisAddressStatus {
  Residence = 0,
  InCountry = 1,
  Emergency = 2,
  Contact = 3,
}

export enum WeightCategory {
  Male = 1,
  Female = 2,
  Child = 3,
}

export interface PassengerEmdCoupon {
  couponNumber: number;
  emdNumber: string;
  emdTypeCode: EmdTypeCode;
  feeKey: string;
  feeType: FeeType;
  flightReference: string;
  numberOfServices: number;
  optionalServiceInformation: OptionalServiceInformation;
  passengerEmdCouponKey: string;
  receivedFromSystemCode: string;
  salesDate?: Date;
  ssrCode: string;
  ssrNumber?: number;
  status: EmdCouponStatus;
}

export enum EmdCouponStatus {
  Unknown = 0,
  HeldConfirmed = 1,
  HoldingNeed = 2,
  HoldingConfirmedEmdRequired = 3,
  HoldingConfirmedEmdIssued = 4,
}

export enum EmdTypeCode {
  None = 0,
  Associated = 1,
  StandAlone = 2,
}

export interface OptionalServiceInformation {
  carrierCode: string;
  reasonForIssuanceSubCode: string;
}

export interface PassengerAssistanceUpdateRequest {
  assistanceServices: AssistanceServices;
  passengerKey: string;
}

export interface AssistanceServices {
  cognitiveDisability?: boolean;
  hasOxygen?: boolean;
  hasServiceAnimal?: boolean;
  isBlind?: boolean;
  isDeaf?: boolean;
  lithiumBatteryWheelchair?: boolean;
  manualWheelchair?: boolean;
  nonSpillBatteryWheelchair?: boolean;
  wetCellBatteryWheelchair?: boolean;
  wheelchairImmobile?: boolean;
  wheelchairNoStairs?: boolean;
  wheelchairToFromGate?: boolean;
}
